import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";
import { usageApi, type UsageDetailsParams } from "../../lib/usage";
import { useUsageAccessScope } from "../../hooks/useUsageAccessScope";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import {
  durationSeconds,
  formatDurationShort,
  formatPercentOfTotal,
} from "./usage-duration";

type UsageDetailsTableProps = {
  filters: Partial<UsageDetailsParams>;
  dateRangeParams: { startDate: string; endDate: string };
  displayMode: "time" | "percent";
  embedded?: boolean;
};

const formatDuration = (
  startTime: string,
  endTime: string | null | undefined,
  active: boolean
): string => {
  const start = new Date(startTime);
  const end = endTime ? new Date(endTime) : active ? new Date() : null;

  if (!end) return "Active";

  const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
};

export default function UsageDetailsTable({
  filters,
  dateRangeParams,
  displayMode,
  embedded = false,
}: UsageDetailsTableProps) {
  const accessScope = useUsageAccessScope();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "startTime", desc: true },
  ]);

  // Convert sorting state to API params
  const sortBy = sorting[0]?.id || "startTime";
  const sortOrder = sorting[0]?.desc ? "desc" : "asc";

  // Narrow filters to the caller's access: drop site/group filters outside scope.
  const scopedFilters: Partial<UsageDetailsParams> = { ...filters };
  let filtersOutsideAccess = false;
  if (
    scopedFilters.siteId &&
    accessScope.allowedSiteIds &&
    !accessScope.allowedSiteIds.has(scopedFilters.siteId)
  ) {
    filtersOutsideAccess = true;
  }
  if (
    scopedFilters.groupId &&
    accessScope.allowedGroupIds &&
    !accessScope.allowedGroupIds.has(scopedFilters.groupId)
  ) {
    filtersOutsideAccess = true;
  }

  const params: UsageDetailsParams = {
    page,
    limit: pageSize,
    sortBy,
    sortOrder,
    ...dateRangeParams,
    ...scopedFilters,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "usage-details",
      params,
      accessScope.role,
      accessScope.ready,
      filtersOutsideAccess,
    ],
    queryFn: () => usageApi.getDetails(params),
    enabled: accessScope.ready && !filtersOutsideAccess,
  });

  const emptyPage = {
    data: [],
    total: 0,
    page: 1,
    limit: pageSize,
    totalPages: 0,
    totalDuration: 0,
  };
  const resolvedData = filtersOutsideAccess ? emptyPage : data;
  const handleSort = (columnId: string) => {
    setSorting((prev) => {
      const existing = prev.find((s) => s.id === columnId);
      if (existing) {
        if (existing.desc) {
          return [{ id: columnId, desc: false }];
        } else {
          return [];
        }
      } else {
        return [{ id: columnId, desc: true }];
      }
    });
  };

  const getSortIcon = (columnId: string) => {
    const sort = sorting.find((s) => s.id === columnId);
    if (!sort) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sort.desc ? (
      <ArrowDown className="h-4 w-4" />
    ) : (
      <ArrowUp className="h-4 w-4" />
    );
  };

  // Calculate total duration from current page data
  const calculateTotalDuration = () => {
    if (!resolvedData?.data || resolvedData.data.length === 0) return 0;

    return resolvedData.data.reduce((total, row) => {
      const seconds = durationSeconds(row);
      if (!seconds) return total;
      return total + seconds;
    }, 0);
  };

  const totalDuration = calculateTotalDuration();

  const totalAllFiltered = resolvedData?.totalDuration ?? 0;

  if (isLoading || !accessScope.ready) {
    return <div className="text-muted-foreground">Loading...</div>;
  }

  if (error) {
    return <div className="text-destructive">Error loading usage details</div>;
  }

  const content = (
    <div className="space-y-4">
      {/* Table */}
      <div className="rounded-md border">
        <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("siteName")}
                  >
                    <div className="flex items-center gap-2">
                      Site
                      {getSortIcon("siteName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="hidden lg:table-cell cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("areaName")}
                  >
                    <div className="flex items-center gap-2">
                      Area
                      {getSortIcon("areaName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("presetName")}
                  >
                    <div className="flex items-center gap-2">
                      Preset
                      {getSortIcon("presetName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("groupName")}
                  >
                    <div className="flex items-center gap-2">
                      Group
                      {getSortIcon("groupName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("userName")}
                  >
                    <div className="flex items-center gap-2">
                      User
                      {getSortIcon("userName")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="hidden lg:table-cell cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("startTime")}
                  >
                    <div className="flex items-center gap-2">
                      Start Time
                      {getSortIcon("startTime")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="hidden lg:table-cell cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("endTime")}
                  >
                    <div className="flex items-center gap-2">
                      End Time
                      {getSortIcon("endTime")}
                    </div>
                  </TableHead>
                  <TableHead
                    className="hidden xl:table-cell cursor-pointer select-none hover:text-foreground"
                    onClick={() => handleSort("active")}
                  >
                    <div className="flex items-center gap-2">
                      Active
                      {getSortIcon("active")}
                    </div>
                  </TableHead>
                  <TableHead className="text-right">
                    {displayMode === "time" ? "Duration" : "% of total"}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {resolvedData?.data && resolvedData.data.length > 0 ? (
                  resolvedData.data.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{row.siteName || "-"}</TableCell>
                      <TableCell className="hidden lg:table-cell">{row.areaName || "-"}</TableCell>
                      <TableCell>{row.presetName || "-"}</TableCell>
                      <TableCell>{row.groupName || "-"}</TableCell>
                      <TableCell>
                        {row.userName || row.userEmail || "-"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {format(new Date(row.startTime), "yyyy-MM-dd HH:mm:ss")}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {row.endTime
                          ? format(new Date(row.endTime), "yyyy-MM-dd HH:mm:ss")
                          : "-"}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell">
                        {row.active === true && (
                          <Badge variant="default">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {displayMode === "time"
                          ? formatDuration(row.startTime, row.endTime, row.active)
                          : formatPercentOfTotal(
                              durationSeconds(row),
                              totalAllFiltered
                            )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow className="bg-muted/10 font-medium">
                  {/* Below lg: 4 columns before Duration (Site, Preset, Group, User) */}
                  <TableCell colSpan={4} className="lg:hidden text-left">
                    {displayMode === "time"
                      ? "Total Duration (page):"
                      : "% of total (page):"}
                  </TableCell>
                  {/* lg to xl: 7 columns before Duration (Site, Area, Preset, Group, User, Start Time, End Time) */}
                  <TableCell colSpan={7} className="hidden lg:table-cell xl:hidden text-left">
                    {displayMode === "time"
                      ? "Total Duration (page):"
                      : "% of total (page):"}
                  </TableCell>
                  {/* xl+: 8 columns before Duration (Site, Area, Preset, Group, User, Start Time, End Time, Active) */}
                  <TableCell colSpan={8} className="hidden xl:table-cell text-left">
                    {displayMode === "time"
                      ? "Total Duration (page):"
                      : "% of total (page):"}
                  </TableCell>
                  <TableCell colSpan={1} className="font-mono text-right">
                    {displayMode === "time"
                      ? formatDurationShort(totalDuration)
                      : formatPercentOfTotal(totalDuration, totalAllFiltered)}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-muted/70 font-medium">
                  {/* Below lg: 4 columns before Duration */}
                  <TableCell colSpan={4} className="lg:hidden text-left">
                    {displayMode === "time"
                      ? "Total Duration (all filtered):"
                      : "% of total (all filtered):"}
                  </TableCell>
                  {/* lg to xl: 7 columns before Duration */}
                  <TableCell colSpan={7} className="hidden lg:table-cell xl:hidden text-left">
                    {displayMode === "time"
                      ? "Total Duration (all filtered):"
                      : "% of total (all filtered):"}
                  </TableCell>
                  {/* xl+: 8 columns before Duration */}
                  <TableCell colSpan={8} className="hidden xl:table-cell text-left">
                    {displayMode === "time"
                      ? "Total Duration (all filtered):"
                      : "% of total (all filtered):"}
                  </TableCell>
                  <TableCell colSpan={1} className="font-mono text-right">
                    {displayMode === "time"
                      ? formatDurationShort(totalAllFiltered)
                      : resolvedData?.totalDuration
                        ? "100%"
                        : "—"}
                  </TableCell>
                </TableRow>
              </TableFooter>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <p className="text-sm text-muted-foreground">
            Showing {(page - 1) * pageSize + 1} to{" "}
            {Math.min(page * pageSize, resolvedData?.total || 0)} of{" "}
            {resolvedData?.total || 0} results
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <p className="text-sm text-muted-foreground">
            Page {page} of {resolvedData?.totalPages || 1}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= (resolvedData?.totalPages || 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return embedded ? content : <div className="p-4">{content}</div>;
}
